















































import { Either, left, right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

// ts3.8以降であればimport typeが利用可能
// @ts-ignore
import { AddToCartProcessStateStore } from '@/components/interactions/cart/add-to-cart/add-to-cart-process-state-store'
import { log } from '@/log'
import { normalErrorMessage } from '@/message/error'
import { DefaultReplacePriceRepository } from '@/models/api/cart/latest-price/replace-price-reposiory'
import { ApiProductclass } from '@/models/api/productclass'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { SkuPriceList } from '@/models/item/sku'
import { DefaultFetchSkuPrice } from '@/models/item/sku/repository/impl/fetch-sku-price'
import { AssociationService } from '@/services/api/external-account-association/association'
import { FkuQuery } from '@/services/api/item/fku/fku-query'
import ProductclassService from '@/services/api/productclass'
import AppId from '@/util/appid'
import { BrandRouteResolver } from '@/util/brandRouteResolver'
import { SizeTagsService } from '@ajax/modules/services/tags/size'
import AddToCartList from '@ajax/vue/components/organisms/addtocart/AddToCartList.vue'
import { AddToCartEvent } from '@ajax/vue/components/organisms/addtocart/view-models/event'
import { ProcessStatus } from '@ajax/vue/components/organisms/addtocart/view-models/process-status'
import { PaidMembershipInteraction } from '@ajax/vue/components/templates/interaction/paid-membership/paid-membership-interaction'
import { CartService } from '@spa/services/api/cart'
import { CartItemListRepository, DefaultCartItemRepository } from '@spa/store/modules/cart/repository'
import { CartItem } from '@spa/store/modules/cart/types'

@Component({
  components: {
    AddToCartList,
  },
})
export default class AddToCartItemList extends Vue {
  /**
   * 初期化時のローディング状態
   */
  isLoading: boolean = false

  /**
   * TODO: {@link AddToCartProcessStateStore} を利用するようにしたい
   */
  addToCartStatus: ProcessStatus = ProcessStatus.asNotProcessing()
  fkuInitializationError: Error[] = []

  fkuList: any[] = []
  skuPriceList: SkuPriceList = SkuPriceList.empty()
  cartItems: CartItem[] = []

  isPaidMember: boolean = false
  isLineAccountAssociated: boolean | null = null

  @Prop({ required: true })
  id: string

  get brandName(): string {
    return BrandRouteResolver.resolveBrandFromPathElement(this.$route.params.brand_english_name)
  }

  get appId(): string {
    return AppId.getByBrandName(this.brandName)
  }

  get behavior(): BehaviorConfig {
    return BehaviorConfig.createFromBrand(this.brandName)
  }

  get itemService(): ProductclassService {
    return new ProductclassService(this.appId)
  }

  get paidMembershipInteraction(): PaidMembershipInteraction {
    return new PaidMembershipInteraction(this.brandName)
  }

  get cartItemListRepository(): CartItemListRepository {
    return DefaultCartItemRepository.createViaApiClient(
      new CartService(this.appId)
    )
  }

  get sizeTagsService(): SizeTagsService {
    return new SizeTagsService(this.brandName, this.appId)
  }

  get paidMembershipFeatureEnabled(): boolean {
    return this.behavior.paidMembership.enabled
  }

  async created() {
    await this.initialize()
  }

  async initialize(): Promise<void> {
    this.isLoading = true

    try {
      await this.initializeCriticalData()
      this.isLoading = false

      await this.initializeOptionalData().catch((error) => {
        log.warn('Optional data initialization failed, but page continues to work', error)
      })
    } catch (error) {
      this.showError(error)

      return
    }
  }

  addDisplayLevel(fkuList: any , sizeTagList: any[]): any[] {
    // add display_level for sort
    _.each(fkuList, (fku) => {
      _.each(fku.parts, (part) => {
        const size = _.find(part.tags, (tag) => {
          return /^company\.product\.size\./.test(tag.pathname)
        })
        if (size) {
          const sizeTag = _.find(sizeTagList, o => o.title === size.label)
          if (sizeTag) {
            _.assign(part, { display_level: sizeTag.display_level })
          }
        }
      })
    })

    return fkuList
  }

  showError(error) {
    this.isLoading = false
    const message = normalErrorMessage
    this.$store.commit('error/set', { message })
    this.$emit('err', error)
    log.error(error)
  }

  get sortedFkuList() {
    return _.sortBy(this.fkuList, 'display_level')
  }

  /**
   * 現時点では、カート追加処理状に関して、一度に一種類のSKUの追加のみが行われる前提を置いている。
   * そのためpayloadに含まれるSKU IDなどの情報は利用していないが、将来的な拡張可能性を想定して受け取っている。
   * @see ProcessStatus
   */
  onStartEvent(_payload: AddToCartEvent): void {
    this.addToCartStatus = ProcessStatus.asProcessing()
  }

  /**
   * 現時点では、カート追加処理状態に関して、一度に一種類のSKUの追加のみが行われる前提を置いている。
   * そのためpayloadに含まれるSKU IDなどの情報は利用していないが、将来的な拡張可能性を想定して受け取っている。
   * @see ProcessStatus
   */
  onStopEvent(_payload: AddToCartEvent): void {
    this.addToCartStatus = ProcessStatus.asNotProcessing()
  }

  onFkuInitializationError(payload: Error): void {
    this.fkuInitializationError.push(payload)
  }

  @Watch('fkuInitializationError', { deep: true })
  showFkuInitializationError() {
    this.showError(
      new Error(
        this.fkuInitializationError.map(error => `[${error.name}]${error.message}`).join(', ')
      )
    )
  }

  private async initializeCriticalData(): Promise<void> {
    const handleError = (error: Error, message: string) => {
      log.error({ message: `Critical API failed - ${message}`, error })
      throw error
    }

    const [
      fkuList,
      isPaidMember,
      sizeTagList,
      cartItems,
    ] = await Promise.all([
      this.fetchAndValidateFkuList().then(result =>
        result.mapLeft(error => handleError(error, 'load FKU list')).value
      ),
      this.paidMembershipInteraction.isPaidMember().then(result =>
        result.mapLeft(error => handleError(error, 'load paid member status')).value
      ),
      this.sizeTagsService.getOrdered('asc').then(result =>
        result.mapLeft(error => handleError(error, 'load size tags')).value
      ),
      this.fetchCartDataIfNeeded().then(result =>
        result.mapLeft(error => handleError(error, 'load cart data')).value
      ),
    ])

    this.isPaidMember = isPaidMember
    this.cartItems = cartItems

    const addedFkuList = this.addDisplayLevel(fkuList, sizeTagList)
    if (!addedFkuList) {
      throw new Error('Failed to process FKU list')
    }
    this.fkuList = addedFkuList

    this.skuPriceList = await this.fetchSkuPrices(addedFkuList).then(
      result => result.mapLeft(
        error => handleError(error, 'SKU price fetch failed')
      ).value
    )
  }

  /**
   * FKUリストを取得し、ブランド情報を検証する
   */
  private async fetchAndValidateFkuList(): Promise<Either<Error, any[]>> {
    const result = await this.itemService.listFkuBelongingToRoot(this.id, FkuQuery.initialize({
      with: 'part,stock~detail',
    }))

    if (result.isLeft()) {
      return left(result.value)
    }

    const validateErrors = this.validateFkuListBrand(result.value)
    if (validateErrors.length > 0) {
      return left(new Error(validateErrors.map(e => e.message).join(', ')))
    }

    return result
  }

  /**
   * FKUリストのブランド情報を検証
   */
  private validateFkuListBrand(fkuList: any[]): Error[] {
    const errors: Error[] = []
    const isNoBrand = (list: any[]): boolean => {
      return list.some((fku) => {
        const brandId: number = _.get(fku, 'brand_id', null)

        return !brandId || brandId.toString().length === 0
      })
    }

    if (isNoBrand(fkuList)) {
      errors.push(new Error('ブランドを登録してください'))
    }

    return errors
  }

  /**
   * 以下では、会員状態に関わらず有効なSKU価格を取得し、下位コンポーネントに渡す準備をしている。
   * TODO: 本来は有効なSKU価格を持ったSKUオブジェクトを受け渡すだけにしたい。
   * 既存がコンポーネントで直接Engineレスポンス型を触っているため、SKUとは別々に有効なSKU価格を受け渡す羽目になっている。
   */
  private async fetchSkuPrices(fkuList: any[]): Promise<Either<Error, SkuPriceList>> {
    const skuList = _.flatten(
      fkuList
      /** 既存ロジックが productclasses: any[]であった。なるべく既存に手を入れず、新設部分でApiProductclassで包んでいる。 */
      .map(fku => new ApiProductclass(fku))
      .map(apiProductclass => apiProductclass.parts)
    )
    const skuIdList = skuList.map(sku => sku.id)

    return DefaultFetchSkuPrice.create(this.brandName).fetch(skuIdList)
  }

  private async fetchCartDataIfNeeded(): Promise<Either<Error, CartItem[]>> {
    // 有料会員機能がONの場合はすでに異なる価格の種類（有料会員価格/非有料会員価格）でカートに入っているか検証する必要がある
    // OFFの場合もカートの取得を行ってもよいが、単に不要なのでパフォーマンスの観点でスキップする
    if (!this.paidMembershipFeatureEnabled) {
      return right([])
    }
    try {
      /**
       * 現在のカートを最新の有効価格に更新する
       * 将来的には単にAPIにカートをリクエストしたら最新の有効価格に更新して返却されるほうが望ましい？
       */
      const replaceRepository = DefaultReplacePriceRepository.create(this.appId)
      await replaceRepository.updateCurrentValidPrice()
      const cartItems = await this.cartItemListRepository.list()

      return right(cartItems)
    } catch (error) {
      log.error({
        message: 'Critical API failed - Cart operations failed',
        error,
      })

      return left(error as Error)
    }
  }

  private async initializeOptionalData(): Promise<void> {
    await this.loadAccountAssociation()
  }

  /**
   * アカウント連携情報の取得
   */
  private async loadAccountAssociation(): Promise<void> {
    return new AssociationService().list(this.brandName, { serviceId: 'line' })
      .then((result) => {
        result.fold(
          (error) => {
            this.isLineAccountAssociated = false
            log.warn('Optional API failed - Business logic error in AssociationService:', error)
          },
          (list) => {
            this.isLineAccountAssociated = list.length > 0
          }
        )
      })
      .catch((error) => {
        this.isLineAccountAssociated = false
        log.warn('Optional API failed - Network/Server error in AssociationService:', error)
      })
  }
}
